import type { ApiResponse, Program } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { invalidateBrandSpaceProgramOptions } from '@/hooks/queries/use-brand-space-program-options-query';
import api from '@/libs/axiosInstance';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';
import { trackProductAction } from '@/libs/product-analytics';

interface CreateProgramPayload {
  name: string;
  description?: string;
  // Add other fields as needed
}

const createProgram = async (payload: CreateProgramPayload): Promise<Program> => {
  const { data } = await api.post<ApiResponse<Program>>('/dashboard/programs', payload);

  if (!data.data) {
    throw new Error(data.message || 'Failed to create program');
  }

  return data.data;
};

export const useCreateProgramAction = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<Program, Error, CreateProgramPayload>({
    mutationFn: (payload) => trackProductAction('programs', 'create', () => createProgram(payload)),
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh the programs list
      queryClient.invalidateQueries({
        queryKey: ['programs'],
      });
      void invalidateBrandSpaceProgramOptions(queryClient);

      toaster.success({
        title: 'Program created successfully',
        description: `${data.name} has been created`,
      });

      // Navigate to the newly created program. The app-wide
      // NavigationHistoryTracker already saved the current page as previousPath.
      navigate(`/programs/${data.id}/overview`);
    },
    onError: (error) => {
      // A plan refusal opens the upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      console.error('Failed to create program:', error);
      toaster.error({
        title: 'Failed to create program',
        description: error.message || 'An unexpected error occurred',
      });
    },
  });
};
